/**
 * Global-Entity IDB persistence.
 *
 * Per-dataspace JSON snapshot of cross-space global entities (Space,
 * Integration, Integration.*, MCPConnection, Org). One IndexedDB database
 * per dataspace, named:
 *
 *   river__global-entities__{dataspaceId}
 *
 * Schema mirrors spaceEntityIDB so the same tooling (storageMonitor, manifest
 * scans, idbCleanup) treats both consistently.
 *
 * The cache is a soft acceleration cache: globalEntityStore.initialize()
 * paints from IDB immediately and kicks off a REST refresh in the background.
 */

import { buildIDBKey } from './idbKeyManager';
import { idbManifest } from './idbManifest';
import { deleteIDBDatabase } from './idbUtils';
import { createFileLogger } from '../../utils/logger';
import type { BaseEntityType } from '../../types/entities';

const log = createFileLogger('GLOBAL_ENTITY_IDB');

const SCHEMA_VERSION = 1;
const DB_VERSION = 1;
const META_STORE = 'meta';
const ENTITIES_STORE = 'entities';
const META_KEY = 'meta';

export interface GlobalEntitiesMeta {
  k: typeof META_KEY;
  fetchedAt: number;
  entityCount: number;
  dataspaceId: string;
  schemaVersion: number;
}

export interface GlobalEntitiesSnapshot {
  meta: GlobalEntitiesMeta;
  entities: BaseEntityType[];
}

/** Stable string for the "no org" / personal dataspace. */
export const PERSONAL_DATASPACE_KEY = '__personal__';

function dbName(dataspaceId: string): string {
  return buildIDBKey('global-entities', dataspaceId || PERSONAL_DATASPACE_KEY);
}

function openDB(dataspaceId: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(dataspaceId), DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'k' });
      }
      if (!db.objectStoreNames.contains(ENTITIES_STORE)) {
        db.createObjectStore(ENTITIES_STORE, { keyPath: '_id' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function awaitReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Read the cached snapshot for a dataspace. Returns null if no cache exists. */
export async function readGlobalSnapshot(
  dataspaceId: string,
): Promise<GlobalEntitiesSnapshot | null> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDB(dataspaceId);
    const tx = db.transaction([META_STORE, ENTITIES_STORE], 'readonly');
    const metaStore = tx.objectStore(META_STORE);
    const entitiesStore = tx.objectStore(ENTITIES_STORE);

    const [meta, entities] = await Promise.all([
      awaitReq(metaStore.get(META_KEY)) as Promise<GlobalEntitiesMeta | undefined>,
      awaitReq(entitiesStore.getAll()) as Promise<BaseEntityType[]>,
    ]);

    await awaitTx(tx).catch(() => {});

    if (!meta || meta.schemaVersion !== SCHEMA_VERSION) {
      return null;
    }

    idbManifest.touchLastAccessed(dbName(dataspaceId)).catch(() => {});

    return { meta, entities: entities ?? [] };
  } catch (err) {
    log.warn(`[GlobalEntityIDB] readSnapshot(${dataspaceId}) failed`, err);
    return null;
  } finally {
    db?.close();
  }
}

/** Atomically replace the snapshot for a dataspace (clear stores then bulk put). */
export async function writeGlobalSnapshot(
  dataspaceId: string,
  entities: BaseEntityType[],
): Promise<void> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDB(dataspaceId);
    const tx = db.transaction([META_STORE, ENTITIES_STORE], 'readwrite');
    const metaStore = tx.objectStore(META_STORE);
    const entitiesStore = tx.objectStore(ENTITIES_STORE);

    entitiesStore.clear();

    for (const entity of entities) {
      if (!entity._id) continue;
      entitiesStore.put(entity);
    }

    const meta: GlobalEntitiesMeta = {
      k: META_KEY,
      fetchedAt: Date.now(),
      entityCount: entities.length,
      dataspaceId: dataspaceId || PERSONAL_DATASPACE_KEY,
      schemaVersion: SCHEMA_VERSION,
    };
    metaStore.put(meta);

    await awaitTx(tx);

    await idbManifest.register(dbName(dataspaceId), 'global-entities', dataspaceId || PERSONAL_DATASPACE_KEY);
  } catch (err) {
    log.warn(`[GlobalEntityIDB] writeSnapshot(${dataspaceId}) failed`, err);
  } finally {
    db?.close();
  }
}

/** Patch a single entity in the cache (called when org Y.Doc node patches arrive). */
export async function patchGlobalEntity(
  dataspaceId: string,
  entity: BaseEntityType,
): Promise<void> {
  if (!entity._id) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDB(dataspaceId);
    const tx = db.transaction([ENTITIES_STORE], 'readwrite');
    tx.objectStore(ENTITIES_STORE).put(entity);
    await awaitTx(tx);
    idbManifest.touchLastAccessed(dbName(dataspaceId)).catch(() => {});
  } catch (err) {
    log.warn(`[GlobalEntityIDB] patchEntity(${dataspaceId}, ${entity._id}) failed`, err);
  } finally {
    db?.close();
  }
}

/** Remove a single entity from the cache (called for delete CRDT events). */
export async function deleteGlobalEntity(
  dataspaceId: string,
  entityId: string,
): Promise<void> {
  if (!entityId) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDB(dataspaceId);
    const tx = db.transaction([ENTITIES_STORE], 'readwrite');
    tx.objectStore(ENTITIES_STORE).delete(entityId);
    await awaitTx(tx);
    idbManifest.touchLastAccessed(dbName(dataspaceId)).catch(() => {});
  } catch (err) {
    log.warn(`[GlobalEntityIDB] deleteEntity(${dataspaceId}, ${entityId}) failed`, err);
  } finally {
    db?.close();
  }
}

/** Drop the snapshot for a dataspace (and unregister from the manifest). */
export async function deleteGlobalSnapshot(dataspaceId: string): Promise<void> {
  const name = dbName(dataspaceId);
  await deleteIDBDatabase(name);
  await idbManifest.unregister(name);
}
